import React, { useEffect, useRef } from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { CircularProgress } from '@mui/material';
import { ChainOfThought } from './ChainOfThought';
import type { CardState, TimelineNode } from './interfaces';
import {
  attachReasoningToToolCalls,
  flattenNodes,
  getTimelineNodeKey,
  groupParallelNodes,
} from './utils.timeline';

function nodeToStatus(node: TimelineNode): 'complete' | 'active' | 'pending' {
  switch (node.kind) {
    case 'reasoning':
      if (node.streaming) return 'active';
      return node.status === 'completed' ? 'complete' : node.status === 'pending' ? 'pending' : 'active';
    case 'scope':
      return node.block.status === 'completed'
        ? 'complete'
        : node.block.status === 'pending'
          ? 'pending'
          : 'active';
    case 'tool_call':
      return node.block.status === 'completed'
        ? 'complete'
        : node.block.status === 'pending' || node.block.status === 'cancelled'
          ? 'pending'
          : 'active';
    case 'message':
      return node.block.streaming ? 'active' : 'complete';
    case 'error':
      return 'complete';
    case 'task':
      return node.block.status === 'completed' ? 'complete' : 'active';
    case 'parallel_group':
      return 'active';
  }
}

function formatDuration(ms: number): string {
  const seconds = Math.round(ms / 1000);
  return seconds === 1 ? '1 second' : `${seconds} seconds`;
}

/**
 * Full tool name overrides (matched after stripping -tool/_tool suffix, case-insensitive).
 * Format: cleaned_name → [active-label, completed-label]
 */
const TOOL_NAME_OVERRIDES: Record<string, [string, string]> = {
  write_todos: ['Updating todos', 'Updated todos'],
  'write-todos': ['Updating todos', 'Updated todos'],
};

/** Irregular verb forms: verb → [present-continuous, past-tense] */
const IRREGULAR_VERBS: Record<string, [string, string]> = {
  read: ['Reading', 'Read'],
  find: ['Finding', 'Found'],
  get: ['Getting', 'Got'],
  run: ['Running', 'Ran'],
  write: ['Writing', 'Wrote'],
  send: ['Sending', 'Sent'],
  build: ['Building', 'Built'],
  think: ['Thinking', 'Thought'],
  split: ['Splitting', 'Split'],
  set: ['Setting', 'Set'],
  put: ['Putting', 'Put'],
  cut: ['Cutting', 'Cut'],
  make: ['Making', 'Made'],
  begin: ['Beginning', 'Began'],
};

/**
 * Convert a snake_case / kebab-case tool name into a human-readable label.
 *   read_file         → Reading file / Read file
 *   search-knowledge-base-tool → Searching knowledge base / Searched knowledge base
 */
function humanizeToolName(raw: string, isComplete: boolean): string {
  const cleaned = raw.replace(/[-_]tool$/i, '');
  const override = TOOL_NAME_OVERRIDES[cleaned.toLowerCase()];
  if (override) return isComplete ? override[1] : override[0];
  const words = cleaned.split(/[-_]/).filter(Boolean);
  if (words.length === 0) return raw;

  const verb = words[0]!.toLowerCase();
  const rest = words.slice(1).join(' ');

  const irregular = IRREGULAR_VERBS[verb];
  if (irregular) {
    const prefix = isComplete ? irregular[1] : irregular[0];
    return rest ? `${prefix} ${rest}` : prefix;
  }

  // Regular verbs: handle doubling consonant, trailing-e, etc.
  const activeVerb = verb.endsWith('e') ? `${verb.slice(0, -1)}ing` : `${verb}ing`;
  const pastVerb = verb.endsWith('e') ? `${verb}d` : `${verb}ed`;
  const prefix = isComplete ? capitalize(pastVerb) : capitalize(activeVerb);

  return rest ? `${prefix} ${rest}` : prefix;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function summarizeInput(input: unknown): string | undefined {
  if (input == null) return undefined;
  if (typeof input === 'string') return input.length > 80 ? `${input.slice(0, 80)}...` : input;
  if (typeof input === 'object') {
    const entries = Object.entries(input as Record<string, unknown>).slice(0, 2);
    if (entries.length === 0) return undefined;
    return entries.map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`).join(', ');
  }
  return String(input);
}

// function summarizeOutput(output: unknown): string | undefined {
//   if (output == null) return undefined;
//   if (typeof output === 'string') return output.length > 100 ? `${output.slice(0, 100)}...` : output;
//   if (typeof output === 'object' && output !== null) {
//     const obj = output as Record<string, unknown>;
//     if (typeof obj.content === 'string') {
//       return obj.content.length > 100 ? `${obj.content.slice(0, 100)}...` : obj.content;
//     }
//     if (typeof obj.status === 'string' && typeof obj.content === 'string') {
//       return `${obj.status}: ${obj.content.slice(0, 80)}`;
//     }
//   }
//   return undefined;
// }

// ---------------------------------------------------------------------------
// Node → label / description
// ---------------------------------------------------------------------------

function nodeLabel(node: TimelineNode): string {
  switch (node.kind) {
    case 'reasoning': {
      const isComplete = node.status === 'completed' && !node.streaming;
      if (isComplete) {
        return node.durationMs !== undefined ? `Thought for ${formatDuration(node.durationMs)}` : 'Thought';
      }
      if (node.summary && node.summary !== 'Thinking...') {
        return node.summary;
      }
      return 'Reasoning...';
    }
    case 'scope':
      return node.block.name;
    case 'tool_call':
      return humanizeToolName(node.block.name, node.block.status === 'completed');
    case 'message':
      return 'Response';
    case 'error':
      return node.block.message;
    case 'task': {
      const lastStep = node.block.steps[node.block.steps.length - 1];
      return lastStep?.displayValue ?? '';
    }
    case 'parallel_group':
      return '';
  }
}

function nodeDescription(_node: TimelineNode): string | undefined {
  return undefined;
}

// function nodeResultSummary(node: TimelineNode): string | undefined {
//   if (node.kind === 'tool_call' && node.block.status === 'completed') {
//     return summarizeOutput(node.block.output);
//   }
//   return undefined;
// }

const CompletedCheckIcon = () => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
    <circle cx={12} cy={12} r={10} stroke="currentColor" strokeWidth={2} fill="none" />
    <path
      d="M8 12.5l2.5 2.5L16 9.5"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

function stepIcon(node: TimelineNode, status: 'complete' | 'active' | 'pending'): React.ReactNode {
  if (node.kind === 'error') return <ErrorOutlineIcon sx={{ fontSize: 18 }} />;

  if (status === 'complete') return <CompletedCheckIcon />;
  return <CircularProgress size={16} thickness={5} sx={{ color: 'text.secondary' }} />;
}

function TimelineStep({ node, isLast }: { node: TimelineNode; isLast: boolean }) {
  if (node.kind === 'parallel_group') {
    return (
      <ChainOfThought.ParallelGroup>
        {node.children.map((child, i) => (
          <TimelineStep
            key={getTimelineNodeKey(child)}
            node={child}
            isLast={isLast && i === node.children.length - 1}
          />
        ))}
      </ChainOfThought.ParallelGroup>
    );
  }

  const status = nodeToStatus(node);
  const label = nodeLabel(node);
  const description = nodeDescription(node);
  // const resultSummary = nodeResultSummary(node);
  const icon = stepIcon(node, status);

  const reasoningText = node.kind === 'tool_call' ? node.reasoningText : undefined;
  const inputFallback = node.kind === 'tool_call' ? summarizeInput(node.block.input) : undefined;

  return (
    <ChainOfThought.Step icon={icon} label={label} description={description} status={status} isLast={isLast}>
      {reasoningText ? (
        <ChainOfThought.CollapsibleDescription text={reasoningText} fallback={inputFallback} />
      ) : null}
    </ChainOfThought.Step>
  );
}

function isCancelledOrPending(node: TimelineNode): boolean {
  if (node.kind === 'tool_call') return node.block.status === 'cancelled' || node.block.status === 'pending';
  if (node.kind === 'reasoning') return node.status === 'cancelled' || node.status === 'pending';
  if (node.kind === 'scope') return node.block.status === 'cancelled' || node.block.status === 'pending';
  if (node.kind === 'task') return node.block.status === 'cancelled' || node.block.status === 'pending';
  return false;
}

function TimelineStepList({ nodes, isCancelled }: { nodes: TimelineNode[]; isCancelled: boolean }) {
  if (!isCancelled) {
    return (
      <>
        {nodes.map((node, i) => (
          <TimelineStep key={getTimelineNodeKey(node)} node={node} isLast={i === nodes.length - 1} />
        ))}
      </>
    );
  }

  // Split into before/after cancellation point
  const lastNonCancelledIdx = nodes.reduce((last, node, i) => (isCancelledOrPending(node) ? last : i), -1);

  const beforeCancellation = nodes.slice(0, lastNonCancelledIdx + 1);
  const afterCancellation = nodes.slice(lastNonCancelledIdx + 1);

  return (
    <>
      {beforeCancellation.map((node, i) => (
        <TimelineStep
          key={getTimelineNodeKey(node)}
          node={node}
          isLast={afterCancellation.length === 0 && i === beforeCancellation.length - 1}
        />
      ))}
      {afterCancellation.length > 0 ? (
        <>
          <ChainOfThought.CancellationDivider />
          {afterCancellation.map((node, i) => (
            <TimelineStep
              key={getTimelineNodeKey(node)}
              node={node}
              isLast={i === afterCancellation.length - 1}
            />
          ))}
        </>
      ) : (
        <ChainOfThought.CancellationDivider />
      )}
    </>
  );
}

export interface AgentProgressTimelineProps {
  className?: string;
  nodes: TimelineNode[];
  cardState?: CardState;
}

export function AgentProgressTimeline({ className, nodes, cardState }: AgentProgressTimelineProps) {
  const isTerminal = cardState?.phase === 'complete' || cardState?.phase === 'cancelled';
  const [open, setOpen] = React.useState(!isTerminal);
  const prevPhaseRef = useRef(cardState?.phase);

  // Auto-collapse when phase transitions to 'complete' or 'cancelled'
  useEffect(() => {
    const wasTerminal = prevPhaseRef.current === 'complete' || prevPhaseRef.current === 'cancelled';
    if (isTerminal && !wasTerminal && prevPhaseRef.current !== undefined) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOpen(false);
    }
    prevPhaseRef.current = cardState?.phase;
  }, [cardState?.phase, isTerminal]);

  if (nodes.length === 0) {
    return null;
  }

  const flat = flattenNodes(nodes);
  const withReasoning = attachReasoningToToolCalls(flat);
  const grouped = groupParallelNodes(withReasoning);
  const isCancelled = cardState?.phase === 'cancelled';

  return (
    <ChainOfThought.Root open={open} onOpenChange={setOpen} className={className}>
      <ChainOfThought.Header
        phase={cardState?.phase ?? 'thinking'}
        subtitle={cardState?.headerSubtitle}
        completedSteps={cardState?.completedSteps}
        totalSteps={cardState?.totalSteps}
      >
        {cardState?.headerLabel}
      </ChainOfThought.Header>
      <ChainOfThought.Content>
        <TimelineStepList nodes={grouped} isCancelled={isCancelled} />
        {cardState?.summaryMetadata && cardState.phase === 'complete' ? (
          <ChainOfThought.StepResult>{cardState.summaryMetadata}</ChainOfThought.StepResult>
        ) : null}
      </ChainOfThought.Content>
    </ChainOfThought.Root>
  );
}
